import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type { PageResponse } from '@/lib/api/types'
import type {
  AccessRevocationResult, ClearanceItemView, ExitCaseView, NoticeRuleView, TemplateItemView,
} from '../types'

const BASE = '/v1/exit'

export function useExitCases(params: {
  entityId?: string; status?: string; exitType?: string; q?: string
  page?: number; size?: number
}) {
  return useQuery({
    queryKey: ['exit', 'cases', params],
    queryFn: async () =>
      (await apiClient.get<PageResponse<ExitCaseView>>(`${BASE}/cases`, {
        params: {
          entityId: params.entityId || undefined,
          status: params.status || undefined,
          exitType: params.exitType || undefined,
          q: params.q || undefined,
          page: params.page,
          size: params.size,
        },
      })).data,
    placeholderData: keepPreviousData,
  })
}

export function useExitCase(id: string | undefined) {
  return useQuery({
    queryKey: ['exit', 'case', id],
    queryFn: async () => (await apiClient.get<ExitCaseView>(`${BASE}/cases/${id!}`)).data,
    enabled: Boolean(id),
  })
}

export function useClearance(caseId: string | undefined) {
  return useQuery({
    queryKey: ['exit', 'clearance', caseId],
    queryFn: async () =>
      (await apiClient.get<ClearanceItemView[]>(`${BASE}/cases/${caseId!}/clearance`)).data,
    enabled: Boolean(caseId),
  })
}

export function useNoticeRules() {
  return useQuery({
    queryKey: ['exit', 'notice-rules'],
    queryFn: async () => (await apiClient.get<NoticeRuleView[]>(`${BASE}/notice-rules`)).data,
  })
}

export function useClearanceTemplate() {
  return useQuery({
    queryKey: ['exit', 'clearance-template'],
    queryFn: async () =>
      (await apiClient.get<TemplateItemView[]>(`${BASE}/clearance-template`)).data,
  })
}

export function useExitMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['exit'] })

  return {
    openCase: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<ExitCaseView>(`${BASE}/cases`, body)).data,
      onSuccess: invalidate,
    }),
    submitCase: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<ExitCaseView>(`${BASE}/cases/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    buildClearance: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<ClearanceItemView[]>(`${BASE}/cases/${id}/clearance`)).data,
      onSuccess: invalidate,
    }),
    addLine: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.post<ClearanceItemView>(`${BASE}/cases/${id}/clearance/lines`, body)).data,
      onSuccess: invalidate,
    }),
    decideLine: useMutation({
      mutationFn: async ({ lineId, ...body }: { lineId: string } & Record<string, unknown>) =>
        (await apiClient.put<ClearanceItemView>(`${BASE}/clearance/${lineId}`, body)).data,
      onSuccess: invalidate,
    }),
    raiseSettlement: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<ExitCaseView>(`${BASE}/cases/${id}/settlement`)).data,
      onSuccess: invalidate,
    }),
    completeCase: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.post<ExitCaseView>(`${BASE}/cases/${id}/complete`, body)).data,
      onSuccess: invalidate,
    }),
    saveRule: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<NoticeRuleView>(`${BASE}/notice-rules/${id}`, body)).data
          : (await apiClient.post<NoticeRuleView>(`${BASE}/notice-rules`, body)).data,
      onSuccess: invalidate,
    }),
    saveTemplateItem: useMutation({
      mutationFn: async ({ id, ...body }: { id?: string } & Record<string, unknown>) =>
        id
          ? (await apiClient.put<TemplateItemView>(`${BASE}/clearance-template/${id}`, body)).data
          : (await apiClient.post<TemplateItemView>(`${BASE}/clearance-template`, body)).data,
      onSuccess: invalidate,
    }),
    revokeDueAccess: useMutation({
      mutationFn: async () =>
        (await apiClient.post<AccessRevocationResult>(`${BASE}/access/revoke-due`)).data,
      onSuccess: invalidate,
    }),
  }
}
